/** Original compact delivery robot for the Adventure art benchmark.
 * The caller owns movement, yaw and collision fitting. All dimensions here are
 * visual authoring data; the robot profile remains the simulation authority. */
import type { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateTorus } from '@babylonjs/core/Meshes/Builders/torusBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { CreateTube } from '@babylonjs/core/Meshes/Builders/tubeBuilder';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AdventureActor } from './adventureCast';

type V3 = readonly [number, number, number];
type ShellRing = readonly [height: number, halfWidth: number, halfDepth: number, z?: number];

/** Continuous injection-moulded surface with a generous lower wheel housing. */
function shellSurface(scene: Scene, name: string, rings: readonly ShellRing[], power = 3.5): Mesh {
  const p:number[]=[],n:number[]=[],uv:number[]=[],ix:number[]=[],segments=64;
  for(const [y,rx,rz,z=0] of rings) for(let j=0;j<=segments;j++) {
    const a=j/segments*Math.PI*2,c=Math.cos(a),s=Math.sin(a);
    p.push(Math.sign(c)*Math.abs(c)**(2/power)*rx,y,z+Math.sign(s)*Math.abs(s)**(2/power)*rz);
    uv.push(j/segments,y*2);
  }
  for(let row=0;row<rings.length-1;row++)for(let j=0;j<segments;j++){
    const a=row*(segments+1)+j,b=a+segments+1;ix.push(a,a+1,b,a+1,b+1,b);
  }
  for(const row of [0,rings.length-1]){const center=p.length/3;p.push(0,rings[row]![0],rings[row]![3]??0);uv.push(.5,.5);
    for(let j=0;j<segments;j++){const a=row*(segments+1)+j;if(row===0)ix.push(center,a+1,a);else ix.push(center,a,a+1);}}
  VertexData.ComputeNormals(p,ix,n);
  for(let row=0;row<rings.length;row++){
    const a=row*(segments+1)*3,b=(row*(segments+1)+segments)*3;
    const v=new Vector3(n[a]!+n[b]!,n[a+1]!+n[b+1]!,n[a+2]!+n[b+2]!).normalize();
    for(const at of[a,b]){n[at]=v.x;n[at+1]=v.y;n[at+2]=v.z;}
  }
  const d=new VertexData();d.positions=p;d.normals=n;d.uvs=uv;d.indices=ix;
  const mesh=new Mesh(name,scene);d.applyToMesh(mesh);return mesh;
}

/** Six bevelled surfaces with actual flat panel centres. Unlike an elongated
 * sphere, its broad faces stay planar; only the perimeter rolls over. */
function softCase(scene: Scene, name: string, size: V3, radius: number, fabric = false): Mesh {
  const positions:number[]=[],normals:number[]=[],uvs:number[]=[],indices:number[]=[];
  const half=size.map(v=>v/2),steps=fabric?12:6;
  const radii=half.map(h=>Math.min(radius,h*.95));
  const coordinate=(step:number,h:number,r:number):number=>{
    if(step===0)return-h;if(step===1)return-h+r*.293;if(step===2)return-h+r;
    if(step===steps)return h;if(step===steps-1)return h-r*.293;if(step===steps-2)return h-r;
    return(-h+r)+(step-2)/(steps-4)*(2*h-2*r);
  };
  for(let axis=0;axis<3;axis++)for(const sign of[-1,1]){
    const a=(axis+1)%3,b=(axis+2)%3,base=positions.length/3;
    for(let row=0;row<=steps;row++)for(let col=0;col<=steps;col++){
      const u=col/steps,v=row/steps,q=[0,0,0];
      q[axis]=sign*half[axis]!;q[a]=coordinate(col,half[a]!,radii[a]!);q[b]=coordinate(row,half[b]!,radii[b]!);
      const inner=q.map((value,i)=>Math.max(-half[i]!+radii[i]!,Math.min(half[i]!-radii[i]!,value)));
      const offset=new Vector3((q[0]!-inner[0]!)/radii[0]!, (q[1]!-inner[1]!)/radii[1]!, (q[2]!-inner[2]!)/radii[2]!).normalize();
      const normal=new Vector3(offset.x/radii[0]!,offset.y/radii[1]!,offset.z/radii[2]!).normalize();
      // A fabric panel sags a few millimetres between its stitched boundaries.
      const bulge=fabric?.0045*Math.sin(u*Math.PI)*Math.sin(v*Math.PI)*(1+.18*Math.cos(u*15+v*9)):0;
      positions.push(inner[0]!+offset.x*radii[0]!+normal.x*bulge,inner[1]!+offset.y*radii[1]!+normal.y*bulge,inner[2]!+offset.z*radii[2]!+normal.z*bulge);
      normals.push(normal.x,normal.y,normal.z);uvs.push(u,v);
    }
    for(let row=0;row<steps;row++)for(let col=0;col<steps;col++){
      const k=base+row*(steps+1)+col,l=k+steps+1;
      if(sign===1)indices.push(k,l,k+1,k+1,l,l+1);else indices.push(k,k+1,l,k+1,l+1,l);
    }
  }
  const data=new VertexData();data.positions=positions;data.normals=normals;data.uvs=uvs;data.indices=indices;
  const mesh=new Mesh(name,scene);data.applyToMesh(mesh);return mesh;
}

/** All artwork below is drawn locally and is owned with this source file. */
function canvasTexture(scene:Scene,name:string,width:number,height:number,draw:(ctx:CanvasRenderingContext2D)=>void):DynamicTexture|null{
  if(typeof document==='undefined')return null;
  const canvas=document.createElement('canvas');canvas.width=width;canvas.height=height;
  draw(canvas.getContext('2d')!);
  const texture=new DynamicTexture(name,canvas,scene,true,Texture.TRILINEAR_SAMPLINGMODE);texture.update(true);return texture;
}

function clothMaps(scene:Scene):{albedo:DynamicTexture|null;normal:DynamicTexture|null;roughness:DynamicTexture|null}{
  const size=256;
  const grain=(x:number,y:number)=>{let v=Math.imul(x+33,374761393)^Math.imul(y+79,668265263);v=Math.imul(v^(v>>>13),1274126177);return((v^(v>>>16))>>>0)/4294967295;};
  const height=(x:number,y:number)=>Math.sin(x*Math.PI/2)*.15+Math.sin(y*Math.PI/2)*.15+grain(x&255,y&255)*.045;
  const albedo=canvasTexture(scene,'adventureOriginalOrangeWeave',size,size,ctx=>{
    const image=ctx.createImageData(size,size);
    for(let y=0;y<size;y++)for(let x=0;x<size;x++){const i=(y*size+x)*4,v=221+height(x,y)*20+grain(x>>3,y>>3)*7;image.data[i]=v;image.data[i+1]=v;image.data[i+2]=v;image.data[i+3]=255;}ctx.putImageData(image,0,0);
  });
  const normal=canvasTexture(scene,'adventureOriginalWeaveNormal',size,size,ctx=>{
    const image=ctx.createImageData(size,size);
    for(let y=0;y<size;y++)for(let x=0;x<size;x++){const i=(y*size+x)*4;image.data[i]=128+(height(x-1,y)-height(x+1,y))*65;image.data[i+1]=128+(height(x,y-1)-height(x,y+1))*65;image.data[i+2]=250;image.data[i+3]=255;}ctx.putImageData(image,0,0);
  });
  const roughness=canvasTexture(scene,'adventureOriginalWeaveRoughness',size,size,ctx=>{
    const image=ctx.createImageData(size,size);
    for(let y=0;y<size;y++)for(let x=0;x<size;x++){const i=(y*size+x)*4;image.data[i]=255;image.data[i+1]=220+grain(x,y)*20;image.data[i+2]=0;image.data[i+3]=255;}ctx.putImageData(image,0,0);
  });
  for(const texture of[albedo,normal,roughness])if(texture){texture.uScale=4;texture.vScale=4;texture.anisotropicFilteringLevel=8;}
  if(normal)normal.gammaSpace=false;if(roughness)roughness.gammaSpace=false;
  return{albedo,normal,roughness};
}

export function buildAdventureRobot(scene: Scene): AdventureActor {
  const root=new TransformNode('adventureHero',scene);
  const body=new TransformNode('adventureHero_body',scene);body.parent=root;
  const meshes:Mesh[]=[],materials:PBRMaterial[]=[],textures:DynamicTexture[]=[];
  function material(name:string,hex:string,roughness:number,metallic=0):PBRMaterial{
    const m=new PBRMaterial('adventureHero_'+name,scene);
    // PBR albedo factors are linear. Converting the art's sRGB swatches avoids
    // the milky, washed-out orange and grey rubber of the previous model.
    m.albedoColor=Color3.FromHexString(hex).toLinearSpace();m.roughness=roughness;m.metallic=metallic;m.environmentIntensity=1;
    materials.push(m);return m;
  }
  const shell=material('ivoryCeramic','#E6E0D3',.33);
  shell.clearCoat.isEnabled=true;shell.clearCoat.intensity=.24;shell.clearCoat.roughness=.32;
  const warmPanel=material('pearlPanels','#EDE7DA',.38),edge=material('panelSeams','#394242',.76);
  const rubber=material('softRubber','#171C1B',.91),tireSide=material('rubberSidewall','#222B29',.88);
  const metal=material('brushedAlloy','#A5ADA7',.31,.8),darkMetal=material('anodizedBracket','#384844',.44,.6);
  const glass=material('smokedGlass','#081312',.14,.07);
  glass.clearCoat.isEnabled=true;glass.clearCoat.intensity=.7;glass.clearCoat.roughness=.08;
  const fabric=material('wovenCarrier','#CF5229',.96),piping=material('fabricPiping','#AA391C',.96);
  const strap=material('wovenStrap','#754733',.98);
  const cyan=material('statusCyan','#35D7C2',.35);cyan.emissiveColor=Color3.FromHexString('#0B6A5D').toLinearSpace();
  const maps=clothMaps(scene);
  for(const texture of[maps.albedo,maps.normal,maps.roughness])if(texture)textures.push(texture);
  fabric.albedoTexture=maps.albedo;fabric.bumpTexture=maps.normal;fabric.metallicTexture=maps.roughness;
  if(fabric.bumpTexture)fabric.bumpTexture.level=.36;
  fabric.useRoughnessFromMetallicTextureAlpha=false;fabric.useRoughnessFromMetallicTextureGreen=true;fabric.useMetallnessFromMetallicTextureBlue=true;
  piping.bumpTexture=maps.normal;piping.albedoTexture=maps.albedo;strap.bumpTexture=maps.normal;
  const add=(mesh:Mesh,mat:PBRMaterial,at:V3=[0,0,0],parent:TransformNode=body):Mesh=>{mesh.material=mat;mesh.position.set(...at);mesh.parent=parent;mesh.isPickable=false;mesh.receiveShadows=true;meshes.push(mesh);return mesh;};
  const panel=(name:string,size:V3,at:V3,mat:PBRMaterial,r=.022,parent:TransformNode=body)=>add(softCase(scene,name,size,r),mat,at,parent);
  const sphere=(name:string,size:V3,at:V3,mat:PBRMaterial,parent:TransformNode=body)=>{const m=CreateSphere(name,{diameter:1,segments:14},scene);m.scaling.set(...size);return add(m,mat,at,parent);};
  const tube=(name:string,path:V3[],radius:number,mat:PBRMaterial,parent:TransformNode=body)=>add(CreateTube(name,{path:path.map(p=>new Vector3(...p)),radius,tessellation:12,cap:Mesh.CAP_ALL},scene),mat,[0,0,0],parent);

  add(shellSurface(scene,'adventureHeroContinuousShell',[
    [.105,.22,.312],[.121,.259,.35],[.157,.279,.364],[.218,.293,.359],
    [.272,.292,.345],[.312,.287,.315],[.357,.283,.284,-.016],
    [.411,.281,.27,-.022],[.484,.276,.254,-.027],[.522,.254,.238,-.022],[.545,.223,.211,-.016],
  ]),shell);
  add(shellSurface(scene,'adventureHeroLowerBumper',[[.086,.20,.30],[.103,.247,.334],[.128,.261,.343],[.141,.251,.334]],3.7),rubber,[0,0,0]);
  // Two continuous panel cuts read as a manufactured shell, with a flush hatch.
  add(shellSurface(scene,'adventureHeroEquatorSeam',[[.273,.2925,.3455],[.277,.2921,.3436],[.280,.2916,.3418]]),edge);
  const rearHatch=panel('adventureRearServiceHatch',[.28,.105,.018],[0,.216,-.347],warmPanel,.024);
  rearHatch.rotation.x=-.10;
  for(const x of[-.12,.12])sphere('adventurePanelFastener',[.009,.009,.004],[x,.238,-.36],metal);
  // Screen face lies just behind its continuous cream bezel and gasket, not on
  // a protruding monitor block. The camera reads a broad dark inset.
  panel('adventureScreenGasket',[.446,.193,.017],[0,.418,-.287],edge,.028);
  panel('adventureFlushDisplay',[.421,.167,.013],[0,.421,-.297],glass,.024);
  panel('adventureDisplayUpperLip',[.38,.010,.017],[0,.511,-.285],warmPanel,.004);
  panel('adventureRearStatus',[.06,.005,.004],[0,.526,-.273],cyan,.002);
  panel('adventureFrontSensorBar',[.31,.045,.023],[0,.332,.293],glass,.016);
  for(const side of[-1,1]){
    panel('adventureSoftCornerBumper',[.105,.053,.017],[side*.2,.131,-.339],rubber,.015);
  }
  const wheels:TransformNode[]=[];
  for(const side of[-1,1])for(const z of[-.263,.263]){
    const wheel=new TransformNode('adventureHero_wheel',scene);wheel.parent=root;wheel.position.set(side*.253,.119,z);wheels.push(wheel);
    const tire=CreateTorus('adventurePneumaticTire',{diameter:.156,thickness:.082,tessellation:32},scene);tire.rotation.z=Math.PI/2;add(tire,rubber,[0,0,0],wheel);
    const sidewall=CreateCylinder('adventureMouldedSidewall',{diameter:.19,height:.061,tessellation:32},scene);sidewall.rotation.z=Math.PI/2;add(sidewall,tireSide,[0,0,0],wheel);
    const rim=CreateCylinder('adventureWheelDisc',{diameter:.136,height:.066,tessellation:32},scene);rim.rotation.z=Math.PI/2;add(rim,metal,[0,0,0],wheel);
    const hub=CreateCylinder('adventureWheelCover',{diameter:.096,height:.071,tessellation:32},scene);hub.rotation.z=Math.PI/2;add(hub,warmPanel,[0,0,0],wheel);
    sphere('adventureWheelHubCap',[.008,.03,.03],[side*.036,0,0],darkMetal,wheel);
  }
  // Compact load brackets stay within the body's width instead of stretching
  // the whole robot narrower when the renderer fits its collision footprint.
  for(const side of[-1,1]){
    sphere('adventureBracketJoint',[.06,.065,.067],[side*.262,.416,-.065],darkMetal);
    tube('adventureLoadBracket',[[side*.261,.425,-.065],[side*.291,.485,-.065],[side*.295,.618,-.055],[side*.278,.681,-.032]],.017,metal);
    tube('adventureBracketCover',[[side*.292,.548,-.059],[side*.293,.617,-.053],[side*.279,.666,-.038]],.025,warmPanel);
    sphere('adventurePackageGrip',[.041,.069,.067],[side*.276,.687,-.028],warmPanel);
  }
  const carrier=new TransformNode('adventureHero_carrier',scene);carrier.parent=body;carrier.position.set(0,.690,-.01);
  add(softCase(scene,'adventureOriginalInsulatedBag',[.548,.28,.456],.026,true),fabric,[0,0,0],carrier);
  panel('adventureCarrierBottomBinding',[.552,.022,.46],[0,-.133,0],piping,.009,carrier);
  panel('adventureCarrierLid',[.558,.039,.464],[0,.137,0],fabric,.014,carrier);
  // Piping follows the actual rounded edge. The front straps visibly support
  // the package and its lid; restrained buckles are functional large shapes.
  for(const z of[-.214,.214])tube('adventureStitchedEdge',[[-.252,-.113,z],[-.265,-.08,z],[-.269,.072,z],[-.252,.116,z]],.004,piping,carrier);
  for(const z of[-.214,.214])tube('adventureStitchedEdge',[[.252,-.113,z],[.265,-.08,z],[.269,.072,z],[.252,.116,z]],.004,piping,carrier);
  for(const x of[-.215,.215]){
    panel('adventureCarrierRetentionStrap',[.026,.279,.005],[x,-.005,-.231],strap,.002,carrier);
    panel('adventureCarrierStrapBuckle',[.037,.027,.008],[x,.074,-.237],darkMetal,.004,carrier);
    panel('adventureCarrierLidStrap',[.026,.006,.447],[x,.158,0],strap,.002,carrier);
  }
  const topTexture=canvasTexture(scene,'adventureOriginalWidePhoGraphic',1024,768,ctx=>{
    ctx.clearRect(0,0,1024,768);ctx.fillStyle='#fff4e0';ctx.textAlign='center';
    ctx.font='900 244px Arial';ctx.fillText('PHỞ',512,306);ctx.font='900 229px Arial';ctx.fillText('NGON',512,562);
  });
  const bowlTexture=canvasTexture(scene,'adventureOriginalBowlMark',512,512,ctx=>{
    ctx.clearRect(0,0,512,512);ctx.strokeStyle='#faefd9';ctx.lineWidth=10;
    for(const x of[197,255,313]){ctx.beginPath();ctx.moveTo(x,135);ctx.bezierCurveTo(x-20,113,x+19,85,x,64);ctx.stroke();}
    ctx.fillStyle='#f5ecd9';ctx.beginPath();ctx.ellipse(256,217,145,34,0,0,Math.PI*2);ctx.fill();
    ctx.beginPath();ctx.ellipse(256,217,137,27,0,0,Math.PI*2);ctx.fillStyle='#B7A24D';ctx.fill();
    ctx.beginPath();ctx.moveTo(111,225);ctx.bezierCurveTo(128,387,384,387,401,225);ctx.closePath();ctx.fillStyle='#fff3db';ctx.fill();
    ctx.strokeStyle='#2D6335';ctx.lineWidth=12;for(const x of[202,239,277,311]){ctx.beginPath();ctx.moveTo(x,218);ctx.lineTo(x+14,205);ctx.stroke();}
    ctx.fillStyle='#fff4e0';ctx.font='bold 52px Arial';ctx.textAlign='center';ctx.fillText('PHỞ NGON',256,451);
  });
  for(const texture of[topTexture,bowlTexture])if(texture){texture.hasAlpha=true;textures.push(texture);}
  const topPrint=material('wideLidPrint','#FFFFFF',.95);topPrint.albedoTexture=topTexture;topPrint.useAlphaFromAlbedoTexture=true;topPrint.transparencyMode=PBRMaterial.PBRMATERIAL_ALPHATEST;
  const frontPrint=material('bowlPrint','#FFFFFF',.95);frontPrint.albedoTexture=bowlTexture;frontPrint.useAlphaFromAlbedoTexture=true;frontPrint.transparencyMode=PBRMaterial.PBRMATERIAL_ALPHATEST;
  const top=CreatePlane('adventureWidePhoNgonTop',{width:.421,height:.342},scene);top.rotation.x=Math.PI/2;add(top,topPrint,[0,.158,0],carrier);
  for(const side of[-1,1]){const label=CreatePlane('adventureBowlCarrierMark',{width:.183,height:.183},scene);if(side===1)label.rotation.y=Math.PI;add(label,frontPrint,[0,-.011,side*.233],carrier);}
  const nameTexture=canvasTexture(scene,'adventureOriginalRobotWordmark',512,128,ctx=>{
    ctx.clearRect(0,0,512,128);ctx.fillStyle='#4d5753';ctx.textAlign='center';ctx.font='56px Arial';ctx.fillText('Asimov',256,90);
  });
  if(nameTexture){nameTexture.hasAlpha=true;textures.push(nameTexture);}
  const wordmark=material('robotWordmark','#FFFFFF',.8);wordmark.albedoTexture=nameTexture;wordmark.useAlphaFromAlbedoTexture=true;wordmark.transparencyMode=PBRMaterial.PBRMATERIAL_ALPHATEST;
  const mark=CreatePlane('adventureRearWordmark',{width:.14,height:.035},scene);add(mark,wordmark,[0,.226,-.362]);
  const tail=new TransformNode('adventureHero_tail',scene);tail.parent=body;tail.position.set(0,.172,-.375);
  sphere('adventureTailSeal',[.073,.078,.028],[0,0,0],edge,tail);
  sphere('adventureSoftTail',[.083,.1,.078],[0,-.006,-.035],warmPanel,tail);
  // Dynamic objects batch by material within their articulation node. Surface
  // colour, normals and UVs remain authored; motion never touches sim state.
  const groups=new Map<TransformNode,Map<PBRMaterial,Mesh[]>>();
  for(const mesh of meshes){const parent=mesh.parent as TransformNode,mat=mesh.material as PBRMaterial;let byMaterial=groups.get(parent);if(!byMaterial){byMaterial=new Map();groups.set(parent,byMaterial);}const group=byMaterial.get(mat)??[];group.push(mesh);byMaterial.set(mat,group);}
  const mergedMeshes:Mesh[]=[];
  for(const [parent,groupsByMat]of groups)for(const [mat,parts]of groupsByMat){
    if(parts.length===1){mergedMeshes.push(parts[0]!);continue;}
    parent.computeWorldMatrix(true);const merged=Mesh.MergeMeshes(parts,true,true,undefined,false,false);
    if(merged){merged.name=parent.name+'_'+mat.name;merged.setParent(parent);merged.isPickable=false;merged.receiveShadows=true;mergedMeshes.push(merged);}
  }
  let lastTime:number|undefined,travelled=0;
  return{root,meshes:mergedMeshes,update(time,speed){
    const dt=lastTime===undefined?0:Math.max(0,Math.min(.1,time-lastTime));lastTime=time;travelled+=dt*speed;
    for(const wheel of wheels)wheel.rotation.x=travelled/.119;
    carrier.rotation.z=Math.sin(travelled*5)*.0025*Math.min(Math.abs(speed),1);
    tail.rotation.z=Math.sin(time*5)*.032*Math.min(Math.abs(speed),1);
  },dispose(){root.dispose(false);for(const m of materials)m.dispose(false,false);for(const t of textures)t.dispose();}};
}
